import { useRef, useState } from "react";

import type { IBaseField } from "../../models/formFields";
import { focusElement } from "../../utils/keyboardFocus";
import {
    getDescribedByElemID,
    getErrorListElemID,
    getHTMLType,
    showValidationErrors,
} from "./formUtils";
import { useFormFieldWarnings } from "./useFormFieldWarnings";
import { useFormValidation } from "./useFormValidation";

/**
 * Comprehensive form field hook that combines all form field functionality
 */
export const useFormField = <
    FieldName extends string,
    IInputType extends HTMLElement,
>(
    props: IBaseField<IInputType, FieldName> & {
        customValidators?: (value: string) => string[];
        inputRef?: React.RefObject<IInputType | null>;
    },
) => {
    const [focused, setFocused] = useState(false);
    const internalRef = useRef<IInputType>(null);
    // Use external ref if provided, otherwise use internal ref
    const inputRef = props.inputRef || internalRef;
    const valueRef = useRef<string>("");

    // Warnings for accessibility
    useFormFieldWarnings(props.id, props.name);

    // Normalize validation to always be an array
    const normalizedValidation = props.validation
        ? Array.isArray(props.validation)
            ? props.validation
            : [props.validation]
        : undefined;

    // Validation hook
    const validation = useFormValidation({
        name: props.name,
        value: props.value,
        validation: normalizedValidation,
        required: props.required,
        onValidStateChange: props.onValidStateChange,
    });

    // Label click handler
    const onLabelClick = () => {
        if (inputRef.current) {
            focusElement(inputRef.current);
        }
    };

    // Event handlers
    const handleChange = (
        event: React.FormEvent<IInputType>,
        customOnChange?: (event: React.FormEvent<IInputType>) => void,
    ) => {
        // Update value ref
        if (
            event.currentTarget instanceof HTMLInputElement ||
            event.currentTarget instanceof HTMLTextAreaElement ||
            event.currentTarget instanceof HTMLSelectElement
        ) {
            valueRef.current = event.currentTarget.value;
        }

        // Run custom onChange if provided
        if (customOnChange) {
            customOnChange(event);
        }

        // Run validators
        validation.runValidators();

        // Call parent onChange
        if (props.onChange) {
            props.onChange(event);
        }
    };

    const handleFocus = (event: React.FocusEvent<IInputType>) => {
        setFocused(true);
        if (props.onFocus) {
            props.onFocus(event);
        }
    };

    const handleBlur = (event: React.FocusEvent<IInputType>) => {
        setFocused(false);
        if (props.onBlur) {
            props.onBlur(event);
        }
    };

    const hasValidationErrors = showValidationErrors(props);

    // Get passthrough input props
    const inputProps = {
        "id": props.id,
        "name": props.name,
        "type": getHTMLType(props.type),
        "className": props.className,
        "autoComplete": props.autoComplete,
        "placeholder": props.placeholder,
        "value": props.uncontrolled
            ? undefined
            : props.value === null
              ? ""
              : props.value,
        "defaultValue": props.uncontrolled
            ? (props.defaultValue ?? props.value ?? undefined)
            : props.defaultValue,
        "required": props.required,
        "disabled": props.disabled,
        // Aria
        "aria-required": validation.isRequired,
        "aria-describedby": props.id
            ? hasValidationErrors
                ? getErrorListElemID(props.id)
                : getDescribedByElemID(props.id)
            : undefined,
        "aria-expanded": props.ariaExpanded,
        "aria-invalid": hasValidationErrors || undefined,
        // Event handlers
        "onKeyPress": props.onKeyPress,
        "onMouseEnter": props.onMouseEnter,
        "onMouseLeave": props.onMouseLeave,
        "onChange": handleChange,
        "onFocus": handleFocus,
        "onBlur": handleBlur,
    };

    return {
        focused,
        inputRef,
        valueRef,
        inputProps,
        isRequired: validation.isRequired,
        runValidators: validation.runValidators,
        getValidators: validation.getValidators,
        onLabelClick,
        handleChange,
        handleFocus,
        handleBlur,
    };
};
